import "./styles/main.scss";

import { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from "react-router";
import { ChakraProvider } from '@chakra-ui/react'
import { ApolloClient, createHttpLink, InMemoryCache, ApolloProvider, gql } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

import GlobalStyles from './styles/GlobalStyles';

import Seo from "./components/seo";
const Home = lazy(() => import("./pages/index").then((module) => ({ default: module.Home }))); 
const NotFound = lazy(() => import("./pages/404").then((module) => ({ default: module.NotFound }))); 
const RecruitmentDatabase = lazy(() => import("./pages/recruitment-database").then((module) => ({ default: module.RecruitmentDatabase }))); 
const WhistleBlowingPolicy = lazy(() => import("./pages/whistle-blowing-policy").then((module) => ({ default: module.WhistleBlowingPolicy }))); 
const Search = lazy(() => import("./pages/search").then((module) => ({ default: module.Search }))); 
const PageEntry = lazy(() => import("./templates/PageEntry").then((module) => ({ default: module.PageEntry }))); 
const WhatWeDoOverviewEntry = lazy(() => import("./templates/WhatWeDoOverviewEntry").then((module) => ({ default: module.WhatWeDoOverviewEntry }))); 
const WhatWeDoAreasOfExpertiseEntry = lazy(() => import("./templates/WhatWeDoAreasOfExpertiseEntry").then((module) => ({ default: module.WhatWeDoAreasOfExpertiseEntry }))); 
const WhatWeDoFocusAreasEntry = lazy(() => import("./templates/WhatWeDoFocusAreasEntry").then((module) => ({ default: module.WhatWeDoFocusAreasEntry }))); 
const WhatWeDoProjectsEntry = lazy(() => import("./templates/WhatWeDoProjectsEntry").then((module) => ({ default: module.WhatWeDoProjectsEntry }))); 
const WhatWeDoTeamEntry = lazy(() => import("./templates/WhatWeDoTeamEntry").then((module) => ({ default: module.WhatWeDoTeamEntry }))); 
const ProjectsListing = lazy(() => import("./templates/ProjectsListing").then((module) => ({ default: module.ProjectsListing }))); 
const ProjectsEntry = lazy(() => import("./templates/ProjectsEntry").then((module) => ({ default: module.ProjectsEntry }))); 
const NewsListing = lazy(() => import("./templates/NewsListing").then((module) => ({ default: module.NewsListing }))); 
const NewsEntry = lazy(() => import("./templates/NewsEntry").then((module) => ({ default: module.NewsEntry }))); 
const NewslettersListing = lazy(() => import("./templates/NewslettersListing").then((module) => ({ default: module.NewslettersListing }))); 
const NewslettersEntry = lazy(() => import("./templates/NewslettersEntry").then((module) => ({ default: module.NewslettersEntry }))); 
const StaffListing = lazy(() => import("./templates/StaffListing").then((module) => ({ default: module.StaffListing }))); 
const StaffEntry = lazy(() => import("./templates/StaffEntry").then((module) => ({ default: module.StaffEntry }))); 
const ResourceListing = lazy(() => import("./templates/ResourceListing").then((module) => ({ default: module.ResourceListing }))); 
const ResourceEntry = lazy(() => import("./templates/ResourceEntry").then((module) => ({ default: module.ResourceEntry }))); 
const GalleryEntry = lazy(() => import("./templates/GalleryEntry").then((module) => ({ default: module.GalleryEntry }))); 

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_CRAFTGQL_URL,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${import.meta.env.VITE_CRAFTGQL_TOKEN}`,
    }
  }
});

const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache()
});

import { initConsent } from './consent';

// Consent Mode defaults must be on the dataLayer before GTM loads, and a stored refusal
// must stop GTM loading at all — so this replaces the two unconditional
// TagManager.initialize calls. (The second one double-loaded GA4: G-D2VZS31TL5 already
// lives inside the GTM-W8Q2P3 container.)
initConsent();

createRoot(document.getElementById('root')).render(
    <ApolloProvider client={client}> 
        <GlobalStyles />
        <ChakraProvider> 
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Suspense fallback={<Seo />}><Home/></Suspense>} />
                    <Route path="/whistle-blowing-policy" element={<Suspense fallback={<Seo />}><WhistleBlowingPolicy/></Suspense>} />
                    <Route path="/recruitment-database" element={<Suspense fallback={<Seo />}><RecruitmentDatabase/></Suspense>} />
                    <Route path="/search" element={<Suspense fallback={<Seo />}><Search/></Suspense>} />

                    <Route path="/what-we-do/:slug" element={<Suspense fallback={<Seo />}><WhatWeDoOverviewEntry/></Suspense>} />
                    <Route path="/what-we-do/:slug/areas-of-expertise" element={<Suspense fallback={<Seo />}><WhatWeDoAreasOfExpertiseEntry/></Suspense>} />
                    <Route path="/what-we-do/:slug/focus-areas" element={<Suspense fallback={<Seo />}><WhatWeDoFocusAreasEntry/></Suspense>} />
                    <Route path="/what-we-do/:slug/projects" element={<Suspense fallback={<Seo />}><WhatWeDoProjectsEntry/></Suspense>} />
                    <Route path="/what-we-do/:slug/team" element={<Suspense fallback={<Seo />}><WhatWeDoTeamEntry/></Suspense>} />
                   
                    <Route path="/news" element={<Suspense fallback={<Seo />}><NewsListing/></Suspense>} />
                    <Route path="/news/page/:page" element={<Suspense fallback={<Seo />}><NewsListing/></Suspense>} />
                    <Route path="/news/:year/:slug" element={<Suspense fallback={<Seo />}><NewsEntry/></Suspense>} />

                    <Route path="/projects" element={<Suspense fallback={<Seo />}><ProjectsListing/></Suspense>} />
                    <Route path="/projects/page/:page" element={<Suspense fallback={<Seo />}><ProjectsListing/></Suspense>} />
                    <Route path="/projects/:slug" element={<Suspense fallback={<Seo />}><ProjectsEntry/></Suspense>} />

                    <Route path="/newsletters" element={<Suspense fallback={<Seo />}><NewslettersListing/></Suspense>} />
                    <Route path="/newsletters/page/:page" element={<Suspense fallback={<Seo />}><NewslettersListing/></Suspense>} />
                    <Route path="/newsletters/:slug" element={<Suspense fallback={<Seo />}><NewslettersEntry/></Suspense>} />

                    <Route path="/reports-and-other-documents" element={<Suspense fallback={<Seo />}><ResourceListing/></Suspense>} />
                    <Route path="/reports-and-other-documents/page/:page" element={<Suspense fallback={<Seo />}><ResourceListing/></Suspense>} />
                    <Route path="/reports-and-other-documents/:slug" element={<Suspense fallback={<Seo />}><ResourceEntry/></Suspense>} />

                    <Route path="/staff" element={<Suspense fallback={<Seo />}><StaffListing/></Suspense>} />
                    <Route path="/staff/page/:page" element={<Suspense fallback={<Seo />}><StaffListing/></Suspense>} />
                    <Route path="/staff/:slug" element={<Suspense fallback={<Seo />}><StaffEntry/></Suspense>} />

                    <Route path="/galleries/:slug" element={<Suspense fallback={<Seo />}><GalleryEntry/></Suspense>} />

                    <Route path="/:slug" element={<Suspense fallback={<Seo />}><PageEntry/></Suspense>} />

                    <Route path="*" element={<Suspense fallback={<Seo />}><NotFound/></Suspense>} />
                </Routes>
            </BrowserRouter>
        </ChakraProvider>
    </ApolloProvider>,
);
